import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface SpeechVoiceContextValue {
    voices: SpeechSynthesisVoice[];
    selectedVoiceURI: string | null;
    selectedVoice: SpeechSynthesisVoice | null;
    setVoiceURI: (voiceURI: string) => void;
}

const SpeechVoiceContext = createContext<SpeechVoiceContextValue | null>(null);

export const SpeechVoiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState<string | null>(null);
    const voicesRef = useRef<SpeechSynthesisVoice[]>([]);

    useEffect(() => {
        const cancelSpeech = () => {
            window.speechSynthesis.cancel();
        };

        cancelSpeech();
        window.addEventListener('beforeunload', cancelSpeech);
        window.addEventListener('pagehide', cancelSpeech);

        return () => {
            window.removeEventListener('beforeunload', cancelSpeech);
            window.removeEventListener('pagehide', cancelSpeech);
            cancelSpeech();
        };
    }, []);

    const pickPreferredVoice = useCallback((voicesList: SpeechSynthesisVoice[]) => {
        const englishVoices = voicesList.filter((v) => v.lang?.toLowerCase().startsWith('en'));
        if (englishVoices.length === 0) return null;

        const byPreference = [...englishVoices].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();

            const score = (name: string) => {
                let s = 0;
                if (name.includes('natural')) s += 50;
                if (name.includes('neural')) s += 40;
                if (name.includes('google')) s += 35;
                if (name.includes('siri')) s += 25;
                if (name.includes('enhanced')) s += 20;
                return s;
            };

            return score(nameB) - score(nameA);
        });

        return byPreference[0] ?? englishVoices[0] ?? null;
    }, []);

    const initVoices = useCallback(() => {
        const list = window.speechSynthesis.getVoices();
        if (!list || list.length === 0) return;

        voicesRef.current = list;
        setVoices(list);

        if (selectedVoiceURI) {
            const chosen = list.find((v) => v.voiceURI === selectedVoiceURI) ?? null;
            if (chosen) return;
        }

        const preferred = pickPreferredVoice(list);
        setSelectedVoiceURI(preferred?.voiceURI ?? null);
    }, [pickPreferredVoice, selectedVoiceURI]);

    useEffect(() => {
        initVoices();
        const handleVoicesChanged = () => initVoices();
        window.speechSynthesis.onvoiceschanged = handleVoicesChanged;
        return () => {
            window.speechSynthesis.onvoiceschanged = null;
        };
    }, [initVoices]);

    const selectedVoice = useMemo(() => {
        if (!selectedVoiceURI) return null;
        return voices.find((v) => v.voiceURI === selectedVoiceURI) ?? null;
    }, [voices, selectedVoiceURI]);

    const setVoiceURI = useCallback((voiceURI: string) => {
        const chosen = voicesRef.current.find((v) => v.voiceURI === voiceURI) ?? null;
        if (!chosen) return;
        setSelectedVoiceURI(chosen.voiceURI);
    }, []);

    const value: SpeechVoiceContextValue = {
        voices,
        selectedVoiceURI,
        selectedVoice,
        setVoiceURI,
    };

    return <SpeechVoiceContext.Provider value={value}>{children}</SpeechVoiceContext.Provider>;
};

export const useSpeechVoice = (): SpeechVoiceContextValue => {
    const ctx = useContext(SpeechVoiceContext);
    if (!ctx) {
        throw new Error('useSpeechVoice must be used within SpeechVoiceProvider');
    }
    return ctx;
};
